import React, { useState } from 'react';
import { DollarSign, ShieldCheck, Info, BookOpen, Layers, ArrowRight } from 'lucide-react';
import { calculateCompoundGrowth } from '../../lib/wealthMathEngine';

export const FeeImpactDcaView: React.FC = () => {
  const [initialDeposit, setInitialDeposit] = useState<number>(10000);
  const [monthlyDeposit, setMonthlyDeposit] = useState<number>(500);
  const [horizonYears, setHorizonYears] = useState<number>(25);
  const [grossReturnPct, setGrossReturnPct] = useState<number>(7.0);
  const [lowFeePct, setLowFeePct] = useState<number>(0.15); // e.g. low-cost ETF
  const [highFeePct, setHighFeePct] = useState<number>(1.25); // e.g. active fund

  const lowFeeResult = calculateCompoundGrowth({
    initialPrincipal: initialDeposit,
    periodicContribution: monthlyDeposit,
    nominalReturnRatePct: grossReturnPct,
    inflationRatePct: 2.5,
    annualFeePct: lowFeePct,
    timeHorizonYears: horizonYears,
    compoundingFrequency: 'monthly',
    contributionFrequency: 'monthly',
    contributionTiming: 'END_OF_PERIOD',
  });

  const highFeeResult = calculateCompoundGrowth({
    initialPrincipal: initialDeposit,
    periodicContribution: monthlyDeposit,
    nominalReturnRatePct: grossReturnPct,
    inflationRatePct: 2.5,
    annualFeePct: highFeePct,
    timeHorizonYears: horizonYears,
    compoundingFrequency: 'monthly',
    contributionFrequency: 'monthly',
    contributionTiming: 'END_OF_PERIOD',
  });

  const feeDifference = lowFeeResult.nominalFutureValue - highFeeResult.nominalFutureValue;

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-semibold border border-indigo-500/30">
            <BookOpen className="w-3.5 h-3.5" />
            <span>YamooPay Fee Impact & DCA Schedule Core</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Long-Term Investment Fee Impact & Dollar-Cost Averaging Tool
          </h1>
          <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
            Quantify how annual management fees erode long-term compound growth over decades and model structured dollar-cost averaging recurring schedules.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
          <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">Fee Comparison Inputs</h2>

          <div className="space-y-4 text-xs text-slate-700">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Initial Principal ($)</label>
                <input
                  type="number"
                  value={initialDeposit}
                  onChange={(e) => setInitialDeposit(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Monthly DCA ($)</label>
                <input
                  type="number"
                  value={monthlyDeposit}
                  onChange={(e) => setMonthlyDeposit(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Gross Annual Return (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={grossReturnPct}
                  onChange={(e) => setGrossReturnPct(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Time Horizon (Years)</label>
                <input
                  type="number"
                  value={horizonYears}
                  onChange={(e) => setHorizonYears(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Low-Fee Option (%)</label>
                <input
                  type="number"
                  step="0.05"
                  value={lowFeePct}
                  onChange={(e) => setLowFeePct(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">High-Fee Option (%)</label>
                <input
                  type="number"
                  step="0.05"
                  value={highFeePct}
                  onChange={(e) => setHighFeePct(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results Column */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <div className="text-xs text-slate-500 font-medium">Lost Growth Attributable to Higher Fees</div>
                <h3 className="text-2xl font-extrabold text-rose-600">
                  -${Math.round(feeDifference).toLocaleString()}
                </h3>
              </div>
              <div className="text-xs text-slate-500 text-right">
                Over {horizonYears} years of continuous investing
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                <div className="font-bold text-emerald-900">Low Fee Structure ({lowFeePct}%)</div>
                <div className="text-xl font-extrabold text-emerald-900">${lowFeeResult.nominalFutureValue.toLocaleString()}</div>
                <div className="text-emerald-700">Total fees deducted: ${lowFeeResult.totalFeesDeducted.toLocaleString()}</div>
              </div>

              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-2">
                <div className="font-bold text-rose-900">High Fee Structure ({highFeePct}%)</div>
                <div className="text-xl font-extrabold text-rose-900">${highFeeResult.nominalFutureValue.toLocaleString()}</div>
                <div className="text-rose-700">Total fees deducted: ${highFeeResult.totalFeesDeducted.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
